import React, { useCallback, useEffect, useState } from 'react';
import Container from 'src/components/shared/Container';
import { TextInput } from 'flowbite-react';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { useMediaQuery, Zoom } from '@mui/material';
import { TypeOfUsers } from 'types';
import { useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import CandidatesHeader from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CandidatesHeader';
import CandidateFilters from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CandidateFilters';
import CampusCandidateListingTabs from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CampusCandidateListingTabs';
import CandidateList from 'src/components/newRecruiterFlow/campus/campusCandidateListing/CandidateList';
import { useGetDriveCandidatesQuery, useGetDrivePositionApplicantsQuery } from 'src/hooks/drives.hooks';
import { TbSearch } from 'react-icons/tb';
import { debounce } from 'lodash';
import DrivePositionSelect from 'src/components/newRecruiterFlow/driveCandidates/DrivePositionSelect';

const DriveCandidatesListingPage = () => {
	const { id } = useParams();
	const [page, setPage] = useState(0);
	const isMobile = useMediaQuery('(max-width:960px)');
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get('search') || '';
	const driveId = searchParams.get('driveId') || '';
	const defaultTab = searchParams.get('tab') || '';
	const [searchQuery, setSearchQuery] = useState('');
	const [totalShortlistedCandidates, setTotalShortlistedCandidates] = useState(0);
	const [tab, setTab] = useState((defaultTab as CandidatesTabsEnum) || CandidatesTabsEnum.RECOMMENDATIONS);
	const [bulkSelectedCandidates, setBulkSelectedCandidates] = React.useState<string[]>([]);
	const changeHandler = (search: string) => {
		searchParams.set('search', search);
		setSearchParams(searchParams);
	};

	const debouncedChangeHandler = useCallback(debounce(changeHandler, 1000), []);

	const { data, isLoading } = useGetDriveCandidatesQuery({
		data: {
			page,
			position: id || '',
			limit: rowsPerPage,
			driveId,
			search: searchParams.get('search') || '',
			sortBy: searchParams.get('sortBy') || 'bestMatch',
			roles: JSON.parse(searchParams.get('role') || '[]'),
			campus: JSON.parse(searchParams.get('campus') || '[]'),
			skills: JSON.parse(searchParams.get('skills') || '[]'),
			salary: JSON.parse(searchParams.get('salary') || '[]'),
			locations: JSON.parse(searchParams.get('location') || '[]'),
			expectedJoining: JSON.parse(searchParams.get('availability') || '[]'),
		},
	});

	const {
		data: applicationsData,
		isLoading: applicationLoading,
		isFetching: applicationFetching,
	} = useGetDrivePositionApplicantsQuery({
		data: {
			page,
			position: id || '',
			limit: rowsPerPage,
			driveId,
			search: searchParams.get('search') || '',
			sortBy: searchParams.get('sortBy') || 'bestMatch',
		},
		onComplete: data => {
			// if (data?.total === 0 && tab !== CandidatesTabsEnum.RECOMMENDATIONS) {
			// 	setSearchParams({ tab: CandidatesTabsEnum.RECOMMENDATIONS, search: searchParams.get('search') || '' });
			// }
		},
	});

	useEffect(() => {
		if (totalShortlistedCandidates && searchParams.get('search')) return;
		setTotalShortlistedCandidates(applicationsData?.total || 0);
	}, [applicationsData, id]);

	const candidates =
		(tab === CandidatesTabsEnum.RECOMMENDATIONS
			? data?.candidates?.filter(e => !e?.application)
			: applicationsData?.candidates?.filter(e => e?.application)) || [];

	return (
		<Container>
			<div>
				<CandidatesHeader
					heading={isMobile ? 'Candidates' : 'Candidates Registered for Drive'}
					extraContent={
						<>
							<div className="ml-2 mr-auto hidden md:block">
								<DrivePositionSelect />
							</div>
							{tab === CandidatesTabsEnum.RECOMMENDATIONS ? (
								<div className="xl:hidden">
									<CandidateFilters
										selectedTab={tab}
										showCampusFilter={true}
										resetPage={() => setPage(0)}
										typeOfUsers={TypeOfUsers.CAMPUS}
										bulkSelectedCandidates={bulkSelectedCandidates}
										setBulkSelectedCandidates={setBulkSelectedCandidates}
										isApplicationApiLoading={applicationLoading || applicationFetching}
										candidates={candidates}
									/>
								</div>
							) : null}
						</>
					}
				/>
			</div>

			<div className="w-full md:hidden mt-2">
				<DrivePositionSelect />
			</div>

			<div className="flex justify-between items-center mt-[10px] pb-[6px] flex-wrap gap-3">
				<Zoom in={true} unmountOnExit>
					<div className="w-full md:max-w-[558px]">
						<CampusCandidateListingTabs
							tab={tab}
							setTab={setTab}
							isTourActive={false}
							onChange={data => {
								setPage(0);
								setBulkSelectedCandidates([]);
								setSearchParams({
									role: searchParams.get('role') || '[]',
									tab: data,
									search: searchQuery,
									...(driveId ? { driveId } : {}),
								});
							}}
							totalConections={totalShortlistedCandidates}
						/>
					</div>
				</Zoom>

				{/* Search Input */}
				<TextInput
					icon={() => <TbSearch color="#C7C9D9" />}
					className="w-full [&_input]:bg-white max-w-full md:max-w-[522px] ml-auto"
					placeholder="Search candidate by name, phone or student code..."
					name="search"
					value={searchQuery}
					onChange={e => {
						setPage(0);
						setSearchQuery(e.target.value);
						debouncedChangeHandler(e.target.value);
					}}
				/>
			</div>

			<div
				className={classNames(
					'mt-[10px]',
					tab === CandidatesTabsEnum.RECOMMENDATIONS ? 'hidden xl:block' : '',
					tab === CandidatesTabsEnum.CONNECTED ? 'hidden' : '',
				)}
			>
				<CandidateFilters
					selectedTab={tab}
					showCampusFilter={true}
					resetPage={() => setPage(0)}
					typeOfUsers={TypeOfUsers.CAMPUS}
					bulkSelectedCandidates={bulkSelectedCandidates}
					setBulkSelectedCandidates={setBulkSelectedCandidates}
					isApplicationApiLoading={applicationLoading || applicationFetching}
					candidates={candidates}
				/>
			</div>
			{(tab === CandidatesTabsEnum.RECOMMENDATIONS && isLoading) ||
			(tab !== CandidatesTabsEnum.RECOMMENDATIONS && applicationLoading) ? (
				<div className="mt-[22px] flex items-center justify-center h-[50vh]">
					<NewCreatingPositionLoader
						showAnimation={true}
						progressBar={false}
						customClassCss="md:!w-[30%] !w-[100%] mx-auto "
					/>
				</div>
			) : (
				<div className="mt-[22px] pb-5">
					<CandidateList
						page={page}
						setPage={setPage}
						selectedTab={tab}
						typeOfUsers={TypeOfUsers.DRIVE}
						count={(tab === CandidatesTabsEnum.RECOMMENDATIONS ? data?.total : applicationsData?.total) || 0}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={setRowsPerPage}
						bulkSelectedCandidates={bulkSelectedCandidates}
						setBulkSelectedCandidates={setBulkSelectedCandidates}
						candidates={candidates}
						totalShortlistedCandidates={totalShortlistedCandidates}
					/>
				</div>
			)}
		</Container>
	);
};

export default DriveCandidatesListingPage;
